/**
 * AppUpdateWatcher — otwarta karta panelu sama dowiaduje się, że stoi nowsza wersja, i wymienia się
 * na nią, gdy to bezpieczne (B-556, Admin 1.602.0).
 *
 * Port `AppUpdateWatcher` z aplikacji kierowcy school-transport (Driver 1.49.0) i `useAppUpdate` z
 * panelu NexOps, w tej samej postaci co `pwa/src/pwa/AppUpdateWatcher.tsx` aplikacji instruktora —
 * z jedną różnicą: panel nie ma service workera, więc samo przeładowanie wystarcza (`index.html` jest
 * rewalidowany, B-482), a komunikat to toast sonner z przyciskiem, nie karta nad paskiem kart.
 *
 * Czym to różni się od B-485 (`odswiezeniePoBleduChunka`): tamto reaguje, GDY karta już się wywróciła
 * na brakującym pliku ekranu; to działa PRZED — karta dowiaduje się o nowej wersji, zanim cokolwiek
 * się zepsuje, i w dobrym momencie.
 *
 * Pyta `/version.json` (plik budowany razem z panelem, serwowany bez cache) po powrocie do karty, po
 * odzyskaniu fokusu i sieci oraz co 15 min. `lib/aktualizacja.ts` rozstrzyga, co z odpowiedzią zrobić:
 *   • reload — powrót do karty po ≥ 30 s, ekran z listy bezpiecznych (`aktualizacjaEkrany.ts`), brak
 *     okna i wpisanego tekstu, nic się nie zapisuje. Nawet wtedy najpierw czeka, aż żądania panelu się
 *     uspokoją, i rozstrzyga jeszcze raz tuż przed przeładowaniem.
 *   • banner — jest inna wersja, ale to nie ten moment: toast „Dostępna nowa wersja panelu” z przyciskiem
 *     „Odśwież teraz” (zostaje, dopóki go nie zamkniesz; zamknięty nie wraca dla tej samej wersji).
 *
 * Milczy, gdy cokolwiek jest niejasne: nieudana albo bezużyteczna odpowiedź niczego nie zmienia.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { APP_VERSION } from '@/lib/version';
import { apiRequestsInFlight } from '@/shared/lib/api';
import { queryClient } from '@/shared/lib/queryClient';
import { czyUiZajete, decideUpdate, readServerVersion, type DocLike } from '@/shared/lib/aktualizacja';
import { isSafeRoute } from '@/shared/lib/aktualizacjaEkrany';

const VERSION_URL = `${import.meta.env.BASE_URL}version.json`;
/** Otwarta i widoczna przez tyle → zapytaj ponownie. */
const CHECK_INTERVAL_MS = 15 * 60_000;
/** `visibilitychange` i `focus` przychodzą parą przy każdym powrocie do karty; jedno pytanie wystarczy.
 *  Sam powrót pyta zawsze. */
const MIN_GAP_MS = 10_000;
const FETCH_TIMEOUT_MS = 8_000;
/** Pierwsze pytanie czeka chwilę, żeby nie konkurować z własnymi żądaniami startowymi strony. */
const FIRST_CHECK_DELAY_MS = 3_000;
/** Odpowiedź, która przychodzi tak krótko po powrocie do karty, nadal liczy się jako „właśnie wróciłem”. */
const RESUME_WINDOW_MS = 20_000;
/** Przed automatycznym przeładowaniem: poczekaj, aż własne żądania się uspokoją (przeładowanie w środku
 *  rotacji refresh-tokena wylogowuje) — ale nie w nieskończoność. */
const QUIET_MAX_MS = 6_000;
/** Przycisk czeka krócej: użytkownik sam o to poprosił. */
const QUIET_MAX_MANUAL_MS = 4_000;
/** sessionStorage: wersja serwera, dla której przeładowanie było już próbowane w tej karcie. */
const GUARD_KEY = 'zur-ops:update.reloadAttemptedFor';
/** sessionStorage: wersja, którą właśnie wymieniamy — po przeładowaniu daje toast „Zaktualizowano”. */
const APPLIED_KEY = 'zur-ops:update.appliedVersion';
const TOAST_ID = 'app-update';

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

function readStore(key: string): string | null {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}
/** false, gdy nie dało się zapisać — bez straży przeładowanie, które nie pomaga, mogłoby się
 *  powtarzać w nieskończoność, więc wołający nie może wtedy przeładować. */
function writeStore(key: string, value: string): boolean {
  try {
    window.sessionStorage.setItem(key, value);
    return window.sessionStorage.getItem(key) === value;
  } catch { return false; }
}
function dropStore(key: string): void {
  try { window.sessionStorage.removeItem(key); } catch { /* nic */ }
}

async function fetchServerVersion(): Promise<string | null> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    /* `no-store` i znacznik czasu: odpowiedź ma pochodzić z serwera za hostem, nie z kopii
       przeglądarki ani pośrednika. */
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      signal: ctrl.signal,
    });
    if (!res.ok) return null;
    return readServerVersion(await res.json());
  } catch {
    return null; // brak sieci, przekroczony czas, HTML portalu — nie ma na czym działać
  } finally {
    window.clearTimeout(timer);
  }
}

/** Dwa spokojne spojrzenia z rzędu, co ćwierć sekundy — albo false po limicie. Spokój = żadne żądanie
 *  API w locie i żaden zapis (mutacja) w toku. */
async function waitForQuiet(maxMs: number): Promise<boolean> {
  const end = Date.now() + maxMs;
  let calm = 0;
  while (Date.now() < end) {
    calm = apiRequestsInFlight() === 0 && queryClient.isMutating() === 0 ? calm + 1 : 0;
    if (calm >= 2) return true;
    await sleep(250);
  }
  return false;
}

function reloadNow(version: string | null): void {
  if (version) writeStore(APPLIED_KEY, version);
  window.location.reload();
}

export default function AppUpdateWatcher() {
  const { pathname } = useLocation();
  /* Nasłuchy poniżej rejestrują się raz; bieżącą trasę czytają stąd. */
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  /** Wersja serwera, dla której użytkownik zamknął komunikat — nie wraca do niej, dopóki nie wyjdzie inna. */
  const dismissedFor = useRef<string | null>(null);
  const reloading = useRef(false);

  const applyNow = useCallback(async (version: string) => {
    if (reloading.current) return;
    reloading.current = true;
    /* Zapis w toku (mutacja) nie może zostać urwany. Po limicie przeładowujemy mimo to — użytkownik
       kliknął, a zapis i tak jest po stronie serwera albo wróci błędem. */
    await waitForQuiet(QUIET_MAX_MANUAL_MS);
    reloadNow(version);
  }, []);

  // Po przeładowaniu wywołanym przez nas: potwierdź, że to zadziałało.
  useEffect(() => {
    const applied = readStore(APPLIED_KEY);
    if (!applied) return;
    dropStore(APPLIED_KEY);
    /* Z opóźnieniem: ten efekt odpala się przed zamontowaniem `Toaster` (jest rodzeństwem aplikacji
       w main.tsx), a sonner gubi toast opublikowany, zanim ktoś go subskrybuje. */
    if (applied !== APP_VERSION) return;
    const timer = window.setTimeout(() => toast.success(`Zaktualizowano do wersji ${APP_VERSION}`), 800);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || import.meta.env.DEV) return;

    let cancelled = false;
    let checking = false;
    /** Każde sprawdzenie bierze numer; nowsze wymuszone unieważnia starsze, więc pytanie, które wisi
     *  przez pobyt w tle (fetch bez odpowiedzi), nie połknie tego zadanego przy powrocie. */
    let seq = 0;
    let lastStart = 0;
    let hiddenAt: number | null = null;
    let resume: { hiddenForMs: number; at: number } | null = null;

    /** null, chyba że to sprawdzenie jest (jeszcze) powrotem do karty. */
    const hiddenForMs = (): number | null =>
      resume && Date.now() - resume.at <= RESUME_WINDOW_MS ? resume.hiddenForMs : null;

    function showToast(serverVersion: string) {
      if (dismissedFor.current === serverVersion) return;
      toast(`Dostępna nowa wersja panelu: ${serverVersion}`, {
        id: TOAST_ID,
        duration: Infinity,
        description: 'Odśwież kartę, żeby przejść na nową wersję. Dopóki tego nie zrobisz, nic się nie zmienia.',
        action: { label: 'Odśwież teraz', onClick: () => { void applyNow(serverVersion); } },
        onDismiss: () => { dismissedFor.current = serverVersion; },
      });
    }

    async function check(force: boolean) {
      if (cancelled || document.visibilityState === 'hidden') return;
      if (!force && (checking || Date.now() - lastStart < MIN_GAP_MS)) return;
      const mine = ++seq;
      const stale = () => cancelled || mine !== seq;
      checking = true;
      lastStart = Date.now();
      try {
        const serverVersion = await fetchServerVersion();
        if (stale() || serverVersion === null) return; // brak odpowiedzi → nic nie zmieniaj

        const input = () => ({
          serverVersion,
          clientVersion: APP_VERSION,
          hiddenForMs: hiddenForMs(),
          safeScreen: isSafeRoute(pathnameRef.current),
          hasUnsyncedWork: queryClient.isMutating() > 0,
          uiBusy: czyUiZajete(document as unknown as DocLike),
          online: navigator.onLine,
          reloadAttemptedFor: readStore(GUARD_KEY),
        });

        let decision = decideUpdate(input());
        if (decision === 'none') {
          toast.dismiss(TOAST_ID);
          return;
        }

        if (decision === 'reload') {
          /* Moment wyglądał na bezpieczny; niech własne żądania panelu się uspokoją i spójrz jeszcze
             raz — użytkownik mógł w międzyczasie coś otworzyć. */
          const quiet = await waitForQuiet(QUIET_MAX_MS);
          if (stale()) return;
          decision = decideUpdate(input());
          if (decision === 'reload' && quiet && document.visibilityState === 'visible' && writeStore(GUARD_KEY, serverVersion)) {
            reloading.current = true;
            reloadNow(serverVersion);
            return;
          }
        }
        showToast(serverVersion);
      } finally {
        if (mine === seq) checking = false;
      }
    }

    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null) {
        resume = { hiddenForMs: Date.now() - hiddenAt, at: Date.now() };
        hiddenAt = null;
      }
      void check(true);
    }
    const onFocus = () => { void check(false); };
    const onOnline = () => { void check(false); };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    const interval = window.setInterval(() => { void check(false); }, CHECK_INTERVAL_MS);
    /* Jeszcze bez `hiddenAt`, więc to pytanie może najwyżej pokazać komunikat — świeżo załadowana strona
       jest już najnowszą rzeczą, jaką serwer wysłał. */
    const first = window.setTimeout(() => { void check(true); }, FIRST_CHECK_DELAY_MS);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.clearInterval(interval);
      window.clearTimeout(first);
    };
  }, [applyNow]);

  return null;
}
